import { useState } from 'react';

import { ButtonVariant } from '@/components/BaseLibrary/Button/Button';
import EcommOptionsList from '@/components/EcommOptionsList/EcommOptionsList';
import FooterComponent from '@/components/Layout/Footer/Footer';
import HeaderComponent from '@/components/Layout/Header/Header';
import { Carousel } from '@/components/ProductPageBase/Carousel/Carousel';
import SunrunShopProductPageBase from '@/components/ProductPageBase/ProductPageBase';
import { Section } from '@/components/Section/Section';
import AddToCartButton from '@/components/SunrunShop/AddToCartButton/AddToCartButton';
import BuyButton from '@/components/SunrunShop/BuyButton/BuyButton';
import type { GlobalFooterContent, GlobalNavContent } from '@/contentful';
import type {
  ProductVariant,
  SunrunShopProductPageEnhancedCF,
} from '@/contentful/api/api.types';
import type { userLocationInterface } from '@/hooks/geo';
import { formatUSD } from '@/utils/currency';
import useStyles from './SunrunShopProductPage.styles';

const NOT_AVAILABLE_BUTTON_LABEL = 'Not available';

interface Props extends userLocationInterface {
  content: SunrunShopProductPageEnhancedCF;
}

const getFirstAvailableVariantId = (
  variants: ProductVariant[] | undefined
): string | undefined => {
  const firstAvailableVariant = variants?.find((v) => v.quantityAvailable);
  return firstAvailableVariant?.id;
};

export default function SunrunShopProductPage({
  content,
  userLocation,
  setUserLocation,
}: Props) {
  const { classes } = useStyles();

  const { fields } = content;
  const { topSections, product, bottomSections, header, footer } = fields;
  const variants = product?.variants?.nodes;

  const [selectedVariantId, setSelectedVariantId] = useState<
    string | undefined
  >(() => getFirstAvailableVariantId(variants));

  const title = product?.title || '';
  const descriptionHtml = product?.descriptionHtml || '';
  const fieldImages =
    fields?.product?.images?.nodes?.map((node) => node.url) || [];
  const subheading = fields.subheading;
  const disclaimer = fields.disclaimer;
  const buyNowButtonLabel = fields?.buyNowButtonLabel;
  const addToCartButtonLabel = fields?.addToCartButtonLabel;

  const [images, setImages] = useState<string[]>(fieldImages);
  const [hoveredImage, setHoveredImage] = useState<string>();

  const selectedVariant = variants?.find((v) => v.id === selectedVariantId);
  const selectedVariantQuantityAvailable = selectedVariant?.quantityAvailable;

  const moveElementToFront = (arr: string[], elementToMove: string) => {
    const index = arr.indexOf(elementToMove);
    if (index >= 0) {
      const [tempElement] = arr.splice(index, 1);
      arr.unshift(tempElement);
    }
    return arr;
  };

  const onSelect = (variantId: string) => {
    setSelectedVariantId(variantId);

    const newVariant = variants?.find((v) => v.id === variantId);
    const urlToMove = newVariant?.image.url;

    if (urlToMove) {
      setImages(moveElementToFront(images, urlToMove));
    }
  };

  const onHover = (hoveredId: string, isEntering: boolean) => {
    if (isEntering) {
      const hoveredVariant = variants?.find((v) => v.id === hoveredId);
      if (hoveredVariant?.quantityAvailable) {
        setHoveredImage(hoveredVariant?.image.url);
      }
    } else {
      setHoveredImage(undefined);
    }
  };

  const isMultiVariant = variants && variants.length > 1;

  return (
    <>
      <HeaderComponent
        header={header as unknown as GlobalNavContent}
        userLocation={userLocation}
        setUserLocation={setUserLocation}
      />
      {topSections?.map((topSection, index) => (
        <Section
          key={topSection.sys.id}
          section={topSection}
          userLocation={userLocation}
          sectionPosition={index}
        />
      ))}
      <SunrunShopProductPageBase
        heading={title}
        subheading={subheading || ''}
        imageContent={
          <Carousel selectedImageOverride={hoveredImage} images={images} />
        }
        descriptionHtml={descriptionHtml}
        optionCardLists={[
          isMultiVariant && (
            <EcommOptionsList
              key='ecomm-options-list'
              heading='Options'
              label='Options'
              name='options'
              items={variants?.map(({ id, title, quantityAvailable }) => ({
                id,
                label: title,
                disabled: !quantityAvailable,
              }))}
              selectedId={selectedVariantId}
              onSelect={onSelect}
              onHover={onHover}
              optionsContainerClassName={classes.optionsContainer}
              cardContentClassName={classes.cardContent}
              disabledCardContentClassName={classes.disabledCardContent}
            />
          ),
        ]}
        totalPrice={
          selectedVariant && formatUSD(selectedVariant.price.amount, 2)
        }
        checkoutButtons={[
          ...(buyNowButtonLabel
            ? [
                <BuyButton
                  key='buy-now'
                  variantId={selectedVariantId}
                  className={classes.buyNowButton}
                  // For single variant, if there is no stock then change button label
                  label={
                    !selectedVariantQuantityAvailable && !isMultiVariant
                      ? NOT_AVAILABLE_BUTTON_LABEL
                      : buyNowButtonLabel
                  }
                />,
              ]
            : []),
          ...(addToCartButtonLabel
            ? [
                <AddToCartButton
                  key='add-to-cart'
                  variantId={selectedVariantId}
                  className={classes.buyNowButton}
                  // For single variant, change button label if there is no stock and no buy now button
                  label={
                    !selectedVariantQuantityAvailable &&
                    !buyNowButtonLabel &&
                    !isMultiVariant
                      ? NOT_AVAILABLE_BUTTON_LABEL
                      : addToCartButtonLabel
                  }
                  variant={
                    buyNowButtonLabel
                      ? ButtonVariant.SECONDARY
                      : ButtonVariant.PRIMARY
                  }
                />,
              ]
            : []),
        ]}
        disclaimer={disclaimer}
      />
      {bottomSections?.map((bottomSection, index) => (
        <Section
          key={bottomSection.sys.id}
          section={bottomSection}
          userLocation={userLocation}
          sectionPosition={index}
        />
      ))}
      <FooterComponent
        footer={footer as unknown as GlobalFooterContent}
        userLocation={userLocation}
        setUserLocation={setUserLocation}
      />
    </>
  );
}
